import mondaySdk from "monday-sdk-js";
import { useEffect } from "react";
import "./FreeMonthOffer.css";

const monday = mondaySdk();

export type OfferStep = "offer" | "redeem";

const BENEFITS = ["10x usage limits", "Unlimited automations", "Unlimited columns sync"];

const VIDEO_FILE = /\.(mp4|webm|mov)(\?|#|$)/i;

interface OfferPanelProps {
  step: OfferStep;
  accepting: boolean;
  acceptError: boolean;
  validUntil: string | null;
  onAccept: () => void;
  onDismiss: () => void;
  onTrack: (event: string) => void;
}

const formatDeadline = (validUntil: string | null) =>
  validUntil
    ? new Date(validUntil).toLocaleDateString(undefined, {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null;

const OfferStepBody = ({
  accepting,
  acceptError,
  onAccept,
  onDismiss,
}: Pick<OfferPanelProps, "accepting" | "acceptError" | "onAccept" | "onDismiss">) => (
  <>
    <div className="flex flex-col gap-2">
      <h2
        id="free-month-offer-title"
        className="m-0 text-2xl font-semibold leading-8 text-[#323338]"
      >
        Your first month of SIMB is free, on any plan
      </h2>
      <p className="m-0 text-base leading-6 text-[#676879]">
        Choose any SIMB plan and pay nothing for the first month. This offer is for the SIMB app
        only. Your monday.com plan stays the same.
      </p>
    </div>

    <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
      {BENEFITS.map((benefit) => (
        <li key={benefit} className="flex items-center gap-2.5 text-base leading-6">
          <span className="free-month-offer__check" aria-hidden="true">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#ffffff"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 12.5l4.5 4.5L19 7.5" />
            </svg>
          </span>
          {benefit}
        </li>
      ))}
    </ul>

    <div className="flex flex-col gap-3">
      {acceptError && (
        <p role="alert" className="m-0 text-sm text-[#d83a52]">
          We couldn't apply your free month. Please try again.
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="free-month-offer__primary"
          onClick={onAccept}
          disabled={accepting}
        >
          {accepting
            ? "Applying your free month…"
            : acceptError
              ? "Try again"
              : "Claim my free month"}
        </button>
        <button
          type="button"
          className="free-month-offer__secondary"
          onClick={onDismiss}
          disabled={accepting}
        >
          No thanks
        </button>
      </div>
      <p className="m-0 text-center text-xs text-[#676879]">
        Applies to monthly billing on any SIMB plan.
      </p>
    </div>
  </>
);

/**
 * The redemption video. A direct file (.mp4/.webm/.mov) plays in a native
 * player; anything else is treated as an embed URL (YouTube, Loom, Vimeo).
 * Renders nothing until REACT_APP_OFFER_VIDEO_URL is set.
 */
const OfferVideo = () => {
  const url = process.env.REACT_APP_OFFER_VIDEO_URL;
  if (!url) return null;

  return VIDEO_FILE.test(url) ? (
    <video
      className="free-month-offer__video"
      data-testid="offer-video"
      src={url}
      controls
      playsInline
      preload="metadata"
    />
  ) : (
    <iframe
      className="free-month-offer__video"
      data-testid="offer-video"
      src={url}
      title="How to redeem your free month of SIMB"
      allow="fullscreen; picture-in-picture"
      allowFullScreen
    />
  );
};

const RedeemStepBody = ({
  validUntil,
  onTrack,
}: Pick<OfferPanelProps, "validUntil" | "onTrack">) => {
  useEffect(() => {
    onTrack("free_month_offer_video_step_viewed");
  }, [onTrack]);

  const deadline = formatDeadline(validUntil);

  const openPlanSelection = () => {
    onTrack("free_month_offer_plan_clicked");
    // Leave the offer modal open: closing it right after this cancels monday's plan screen.
    monday.execute("openPlanSelection", { isInPlanSelection: false });
  };

  return (
    <>
      <div className="flex flex-col gap-2">
        <h2
          id="free-month-offer-title"
          className="m-0 text-2xl font-semibold leading-8 text-[#323338]"
        >
          Your free month of SIMB is reserved
        </h2>
        <p className="m-0 text-base leading-6 text-[#676879]">
          {deadline
            ? `Pick a SIMB plan before ${deadline} and your first month is free.`
            : "Pick a SIMB plan and your first month is free."}{" "}
          Here's how to finish.
        </p>
      </div>

      <OfferVideo />

      <button type="button" className="free-month-offer__primary" onClick={openPlanSelection}>
        Choose a plan
      </button>
    </>
  );
};

/**
 * The offer's content. It fills the monday app feature modal that
 * FreeMonthOffer opens, so it has no dialog chrome of its own: monday draws the
 * frame and the close button.
 */
const OfferPanel = ({ step, ...props }: OfferPanelProps) => (
  <main
    className="free-month-offer"
    data-testid="offer-panel"
    aria-labelledby="free-month-offer-title"
  >
    <div className="free-month-offer__hero" aria-hidden="true">
      <svg className="free-month-offer__mark" viewBox="0 0 168 110">
        <defs>
          <clipPath id="free-month-offer-left">
            <circle cx="55" cy="55" r="55" />
          </clipPath>
          <clipPath id="free-month-offer-right">
            <circle cx="113" cy="55" r="55" />
          </clipPath>
        </defs>
        <circle cx="55" cy="55" r="55" fill="#ffffff" />
        <circle cx="113" cy="55" r="55" fill="#ffffff" />
        <circle
          cx="55"
          cy="55"
          r="53"
          fill="none"
          stroke="#282876"
          strokeWidth="4"
          clipPath="url(#free-month-offer-right)"
        />
        <circle
          cx="113"
          cy="55"
          r="53"
          fill="none"
          stroke="#282876"
          strokeWidth="4"
          clipPath="url(#free-month-offer-left)"
        />
      </svg>
      <div className="relative flex flex-col gap-2">
        <span className="free-month-offer__eyebrow">Same Item Multiple Boards</span>
        <span className="free-month-offer__headline">1 month free</span>
      </div>
    </div>
    <div className="flex flex-col gap-6 px-10 py-8">
      {step === "offer" ? (
        <OfferStepBody {...props} />
      ) : (
        <RedeemStepBody validUntil={props.validUntil} onTrack={props.onTrack} />
      )}
    </div>
  </main>
);

export default OfferPanel;
